// Login.tsx — username/password form for the dev/JWT flow (used when MSAL is
// not configured but the app is talking to the real backend). On success the
// JWT lands in localStorage and the user is bounced back to where they came
// from (or the role-aware landing at / by default — pharmacists → /dispensing).

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Field, Input } from '@/foundation/primitives';
import { devLogin } from '@/auth/devAuth';
import { useTenantBranding, tenantSubdomainFromHost } from '@/auth/tenant';
import WorkspaceRedirect from '@/routes/WorkspaceRedirect';
import { AuthShell, ONBOARDING_URL, authErrorBox, authLink } from './AuthShell';

interface LocState {
  from?: string;
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  // No explicit origin → the root HomeRedirect picks the role-aware landing page (audit #9).
  // Deep links (RequireAuth sets `from`) still return exactly where the user was headed.
  const from = (location.state as LocState | null)?.from ?? '/';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Subdomain branding (W5): theme the login for the tenant whose subdomain we're on.
  const { data: branding, isSuccess: brandingResolved } = useTenantBranding();
  const subdomain = tenantSubdomainFromHost();
  // On a tenant subdomain that resolves but has no workspace, show the redirect splash instead of
  // a generic login (which would just confuse / let them try to sign in to nothing).
  const unknownWorkspace = !!subdomain && brandingResolved && branding === null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await devLogin(username.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (unknownWorkspace) return <WorkspaceRedirect subdomain={subdomain} />;

  return (
    <AuthShell
      title="Welcome back."
      deck="Audit-ready pharmacy operations · MHRA Annex 11 · EU GDP · GDPR"
      workspace={branding?.name}
      footer={
        <>
          New to PharmacyIQ?{' '}
          <a href={ONBOARDING_URL} style={authLink}>
            Start at mypharmacyiq.com
          </a>
        </>
      }
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Username">
          <Input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            placeholder="your username"
          />
        </Field>

        <Field label="Password">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            placeholder="••••••••"
          />
        </Field>

        <div style={{ textAlign: 'right', marginTop: -6 }}>
          <Link to="/forgot-password" style={{ ...authLink, fontSize: 'var(--fs-body)' }}>
            Forgot password?
          </Link>
        </div>

        {error && <div style={authErrorBox}>{error}</div>}

        <Button
          disabled={submitting || !username || !password}
          style={{ width: '100%', justifyContent: 'center', height: 44 }}
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </AuthShell>
  );
}
